import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationStandardProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Col, Input, Modal, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilterSpecialCommissionsForAgentOutputs } from "../../../../../common/api/queries";
import { ModalSizes, PageSizes, rowGutter, TableSizes } from "../../../../../common/constants";
import { formatAgentName } from "../../../../agent/utils";
import { CommissionsOutput } from "../../../batch/types";
import { removeBatchNameCustomSuffix } from "../../../batch/utils";
import { SpecialCommissionsReport } from "../../../special/enums";
import { SpecialCommissionBase } from "../../../special/types";

interface Props {
  commissionsOutput?: CommissionsOutput;
  onCancel: () => void;
  agentId: string;
}

export const OutputSpecialCommissionModal = ({ commissionsOutput, onCancel, agentId }: Props) => {
  const { t } = useTranslation();

  const [code, setCode] = useState<string>();
  const [pageIndex, setPageIndex] = useState<number>(0);

  const { data: outputSpecialCommissions, isFetching } = useFilterSpecialCommissionsForAgentOutputs({
    id1: agentId,
    id2: commissionsOutput?.id ?? "",
    object: { code, report: SpecialCommissionsReport.INCLUDED, pageSize: PageSizes.SMALL, pageIndex: pageIndex },
  });

  const columns: ColumnsType<SpecialCommissionBase> = [
    {
      key: "code",
      title: t("commissions.special.attrs.code"),
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
    },
    {
      key: "name",
      title: t("commissions.special.attrs.name"),
      width: 300,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "payer",
      title: t("commissions.special.attrs.payer"),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatAgentName(record.payer)}</Ellipsis>,
    },
    {
      key: "payee",
      title: t("commissions.special.attrs.payee"),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatAgentName(record.payee)}</Ellipsis>,
    },
    {
      key: "amount",
      title: t("commissions.special.attrs.commissionAmount"),
      width: 100,
      align: "right",
      render: (_, record) =>
        formatLocaleCurrency(
          record.payee.id === commissionsOutput?.gainer?.id ? record.commissionAmount : -record.commissionAmount,
        ),
    },
  ];

  return (
    <Modal
      width={ModalSizes.HUGE}
      title={
        commissionsOutput?.batch
          ? t("user.titles.outputSpecialCommissions") + " " + removeBatchNameCustomSuffix(commissionsOutput.batch.name)
          : t("user.titles.outputSpecialCommissions")
      }
      open={!!commissionsOutput}
      footer={
        <Button onClick={onCancel} icon={<AntIcon type="close" />}>
          {t("common.close")}
        </Button>
      }
      onCancel={onCancel}
      destroyOnHidden
      mask={{ closable: false }}
    >
      <Row gutter={rowGutter}>
        <Col span={24} className="center-align">
          <Input.Search
            style={{ width: "300px" }}
            className="margin-bottom-tiny"
            enterButton
            allowClear
            placeholder={t("commissions.batch.helpers.specialCommissionCodePlaceholder")}
            onSearch={setCode}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <div className={outputSpecialCommissions?.totalElementsCount ? "table-header-margin" : "margin-bottom-small"}>
            <b>{t("commissions.special.helpers.filteredCommissionAmountsSum")}: </b>
            {outputSpecialCommissions?.commissionAmountsSum
              ? formatLocaleCurrency(outputSpecialCommissions.commissionAmountsSum)
              : undefined}
          </div>

          <Table<SpecialCommissionBase>
            {...tableStandardProps()}
            columns={columns}
            scroll={{ x: TableSizes.MEDIUM }}
            dataSource={outputSpecialCommissions?.pageData}
            pagination={{
              ...paginationStandardProps,
              placement: ["topEnd"],
              current: numberOrZero(outputSpecialCommissions?.pageIndex) + 1,
              pageSize: outputSpecialCommissions?.pageSize,
              total: outputSpecialCommissions?.totalElementsCount,
              onChange: (pageIndex) => setPageIndex(pageIndex - 1),
            }}
            loading={isFetching}
          />
        </Col>
      </Row>
    </Modal>
  );
};
